// Widths follow the app's shipped code (SI-2; lib/shell/size_class.dart):
// phone under 700, tablet 700 to 1099, desktop from 1100. The web measures
// the window, since nothing sits beside the app. Inside a pane, the
// switches that depend on the pane's own width use usePaneWidth().
import { onBeforeUnmount, onMounted, reactive, ref } from "vue";

export const PHONE_MAX = 700;
export const DESKTOP_MIN = 1100;

export function sizeClass(width) {
  if (width < PHONE_MAX) return "phone";
  if (width < DESKTOP_MIN) return "tablet";
  return "desktop";
}

// The window's width, kept current while anything listens (AppShell
// installs the listener once).
export const viewport = reactive({
  width: typeof window !== "undefined" ? window.innerWidth : 1280,
  height: typeof window !== "undefined" ? window.innerHeight : 800,
});

let listeners = 0;
const onResize = () => {
  viewport.width = window.innerWidth;
  viewport.height = window.innerHeight;
};

export function trackViewport() {
  if (typeof window === "undefined") return () => {};
  if (listeners++ === 0) window.addEventListener("resize", onResize);
  onResize();
  return () => {
    if (--listeners === 0) window.removeEventListener("resize", onResize);
  };
}

/**
 * The width of an element (a pane), kept current by a ResizeObserver.
 *   const el = ref(null); const width = usePaneWidth(el);
 * Starts at the window's width until the element is measured.
 */
export function usePaneWidth(elRef) {
  const width = ref(viewport.width);
  let ro = null;
  onMounted(() => {
    const el = elRef && elRef.value;
    const node = el && el.$el ? el.$el : el;
    if (!node || typeof ResizeObserver === "undefined") return;
    width.value = node.getBoundingClientRect().width || width.value;
    ro = new ResizeObserver((entries) => {
      for (const e of entries) width.value = e.contentRect.width;
    });
    ro.observe(node);
  });
  onBeforeUnmount(() => {
    if (ro) ro.disconnect();
    ro = null;
  });
  return width;
}
